import { useState, useEffect, useRef } from "react";
import { IconPencil, IconTrash } from "@tabler/icons-react";
import { toast } from "sonner";
import type { CustomerNoteHistory } from "../../api/queries/customerRepresentativeQueries";
import {
  useDeleteCustomerNoteMutation,
  useUpdateCustomerNoteMutation,
} from "../../api/queries/customerRepresentativeQueries";
import { ConfirmationModal } from "../ConfirmationModal";
import { CustomerNoteBody } from "./CustomerNoteBody";
import { MentionTextarea } from "./MentionTextarea";
import { CustomerNoteAttachmentList } from "./CustomerNoteAttachmentList";
import { displayISODate } from "../../utils/display";

type CustomerNoteHistoryItemProps = {
  entry: CustomerNoteHistory;
  customerOrgId: number;
  canModify: boolean;
  highlight?: boolean;
  onUpdated?: () => void;
};

export function CustomerNoteHistoryItem({
  entry,
  customerOrgId,
  canModify,
  highlight = false,
  onUpdated,
}: CustomerNoteHistoryItemProps) {
  const itemRef = useRef<HTMLLIElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(entry.body);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const updateNoteMutation = useUpdateCustomerNoteMutation();
  const deleteNoteMutation = useDeleteCustomerNoteMutation();

  const isSaving = updateNoteMutation.isPending || deleteNoteMutation.isPending;

  useEffect(() => {
    if (highlight && itemRef.current) {
      itemRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlight]);

  const handleSaveEdit = async () => {
    const trimmed = editValue.trim();
    if (!trimmed) return;
    try {
      await updateNoteMutation.mutateAsync({
        customerRepId: customerOrgId,
        noteId: entry.id,
        body: trimmed,
      });
      setIsEditing(false);
      onUpdated?.();
    } catch {
      toast.error("Failed to update note");
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteNoteMutation.mutateAsync({
        customerRepId: customerOrgId,
        noteId: entry.id,
      });
      setDeleteConfirmOpen(false);
      onUpdated?.();
    } catch {
      toast.error("Failed to delete note");
    }
  };

  const handleCancelEdit = () => {
    setEditValue(entry.body);
    setIsEditing(false);
  };

  return (
    <li
      ref={itemRef}
      className={`relative rounded-md transition-colors ${
        highlight ? "bg-blue-50 ring-2 ring-blue-200" : ""
      }`}
    >
      <span className="absolute -left-[1.35rem] top-1 h-2.5 w-2.5 rounded-full border-2 border-white bg-teal-600 ring-2 ring-teal-100" />
      {isEditing ? (
        <div className="space-y-2">
          <MentionTextarea
            value={editValue}
            onChange={setEditValue}
            rows={3}
            className="w-full resize-y rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={handleCancelEdit}
              disabled={isSaving}
              className="cursor-pointer rounded-md border border-slate-300 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveEdit}
              disabled={isSaving || !editValue.trim()}
              className="cursor-pointer rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-60"
            >
              Save
            </button>
          </div>
        </div>
      ) : (
        <>
          {entry.body.trim() ? (
            <p className="whitespace-pre-wrap text-sm text-slate-700">
              <CustomerNoteBody body={entry.body} />
            </p>
          ) : null}
          <CustomerNoteAttachmentList
            customerOrgId={customerOrgId}
            noteId={entry.id}
            attachments={entry.attachments ?? []}
          />
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <p className="text-xs italic text-slate-500">
              {displayISODate(entry.created_at)}
              {entry.author_name ? ` | ${entry.author_name}` : null}
            </p>
            {canModify ? (
              <div className="ml-auto flex gap-1">
                <button
                  type="button"
                  title="Edit note"
                  aria-label="Edit note"
                  onClick={() => {
                    setEditValue(entry.body);
                    setIsEditing(true);
                  }}
                  disabled={isSaving}
                  className="inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-60"
                >
                  <IconPencil size={14} />
                </button>
                <button
                  type="button"
                  title="Delete note"
                  aria-label="Delete note"
                  onClick={() => setDeleteConfirmOpen(true)}
                  disabled={isSaving}
                  className="inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-60"
                >
                  <IconTrash size={14} />
                </button>
              </div>
            ) : null}
          </div>
        </>
      )}
      <ConfirmationModal
        open={deleteConfirmOpen}
        title="Delete this note?"
        description="This note will be removed from the customer history. This action cannot be undone."
        confirmLabel="Delete note"
        isLoading={deleteNoteMutation.isPending}
        onCancel={() => {
          if (!deleteNoteMutation.isPending) setDeleteConfirmOpen(false);
        }}
        onConfirm={handleConfirmDelete}
      />
    </li>
  );
}
